import type { Box, Edge, Node, NodeOrigin, Rect } from '@xyflow/react';

import { boxToRect, getNodePositionWithOrigin, rectToBox } from '@xyflow/system';
import { getConnectionOrVendorType, isSqlAutomation } from 'js/lib/ResourceUtils';
import { isCapsuleCluster } from 'js/lib/PipelineUtils';
import { canActivateResource } from 'js/lib/flowValidationUtils';
import {
  shouldRenderCronAction,
  shouldRenderRunAction,
} from '../cards/components/ExpandActions/utils/shouldRenderSourceAction';
import { FLOW_CANVAS_CONFIG, getDirectionConfig } from './constants';
import { entityByKey } from './consts';
import { PipelineItems } from './types';

export const getBoundsOfBoxes = (box1: Box, box2: Box): Box => ({
  x: Math.min(box1.x, box2.x),
  y: Math.min(box1.y, box2.y),
  x2: Math.max(box1.x2, box2.x2),
  y2: Math.max(box1.y2, box2.y2),
});

export const getRelativeNodesBounds = (nodes: Node[], nodeOrigin: NodeOrigin = [0, 0]): Rect => {
  if (nodes.length === 0) {
    return { x: 0, y: 0, width: 0, height: 0 };
  }

  const box = nodes.reduce(
    (currBox, node) => {
      const { x, y } = getNodePositionWithOrigin(node, nodeOrigin);
      return getBoundsOfBoxes(
        currBox,
        rectToBox({
          x,
          y,
          width: (node.measured.width || 0) + 40,
          height: (node.measured.height || 0) + 40,
        }),
      );
    },
    { x: Infinity, y: Infinity, x2: -Infinity, y2: -Infinity },
  );

  return boxToRect(box);
};

export function shouldRenderNode(item: any, nodeMap: PipelineItems): boolean {
  // If it's a flow node, we need to render it
  return !!(
    Object.keys(entityByKey).includes(item?.type) ||
    (nodeMap[item?.flow_node_id] && item) ||
    (nodeMap[item?.id] && item)
  );
}

/**
 * Webhook source non-operational and hidden from the canvas
 */
export function isHiddenWebhookResponseSource(item: any, pipelineItems: PipelineItems): boolean {
  if (item?.type !== 'SOURCE') return false;
  const connectorType = item.source_type ?? item.connector_type;
  const isIncomingWebhook = connectorType === 'nexla_rest';
  if (!isIncomingWebhook) return false;
  const itemKey = item.flow_node_id ?? item.id;
  const isChildOfSink =
    item.data_sink_id != null ||
    Object.values(pipelineItems).some(
      (p: any) =>
        p?.type === 'DATA_SINK' &&
        p?.children?.some((c: any) => c === itemKey || c === String(itemKey)),
    );
  return isChildOfSink;
}

/**
 * Returns a new items object with webhook response sources (child of sink) removed.
 */
export function filterWebhookResponseSourcesFromItems(items: PipelineItems): PipelineItems {
  const keysToRemove = Object.keys(items).filter((key) =>
    isHiddenWebhookResponseSource(items[key], items),
  );
  if (keysToRemove.length === 0) return items;
  const result = { ...items };
  keysToRemove.forEach((key) => delete result[key]);
  return result;
}

type BuildControlNodesProps = {
  pipelineItems: PipelineItems;
  newNodes: Node<any>[];
  newEdges: Edge[];
  props: any;
  nodeProps: any;
  vendors: Record<string, any>;
  metaInfoById: Record<string, any>;
  layoutDirection?: 'LR' | 'TB';
  entities?: { flowNodes?: Record<string, any>; sources?: Record<string, any> };
  branches?: Record<string, { items?: Array<string | number> }>;
};

/** Matches SOURCE rows however the merged pipeline item encodes type/node_type. */
export function isPipelineSourceItem(item: any): boolean {
  if (!item) return false;
  if (item.type === 'SOURCE' || item.type === 'data_source') return true;
  if (item.node_type === 'data_source' && item.type !== 'DATA_SET' && item.type !== 'DATA_SINK') {
    return true;
  }
  return false;
}

/** Stable id suffix for schedule canvas nodes (`run-now-*`, `cron-*`). */
export function getScheduleCanvasGraphKey(
  sourceItem: any,
  canvasFlowRootId?: string | number | null,
): string {
  if (sourceItem?.origin_node_id != null) {
    return String(sourceItem.origin_node_id);
  }
  if (canvasFlowRootId != null && canvasFlowRootId !== '') {
    return String(canvasFlowRootId);
  }
  return String(sourceItem?.flow_node_id ?? sourceItem?.id ?? '');
}

export function buildMergedFlowDataForSchedule(
  sourceItem: any,
  entitiesFlowNodes?: Record<string, any>,
  entitiesSources?: Record<string, any>,
): any {
  const oid = sourceItem?.origin_node_id;
  const flowRow = oid != null && entitiesFlowNodes ? entitiesFlowNodes[oid] : undefined;
  // Merge with full source entity from Redux for complete config data
  // (pipeline items may lack source_config, template_config, etc.)
  const sourceEntity =
    sourceItem?.id && entitiesSources ? entitiesSources[sourceItem.id] : undefined;
  return {
    ...(flowRow || {}),
    ...(sourceEntity || {}),
    ...sourceItem,
    // Prefer source entity's config fields over pipeline item's
    source_config: sourceEntity?.source_config ?? sourceItem?.source_config,
    template_config: sourceEntity?.template_config ?? sourceItem?.template_config,
    flow_type: flowRow?.flow_type ?? sourceItem?.flow_type,
    excludeFlowControlNode: flowRow?.excludeFlowControlNode ?? sourceItem?.excludeFlowControlNode,
  };
}

export function getScheduleCanvasVisibility(
  flowData: any,
  vendors: Record<string, any>,
  metaInfoById: Record<string, any>,
  hasDestination = true,
  byoV2Enabled = false,
): {
  shouldRenderRun: boolean;
  shouldRenderCron: boolean;
  connectionCategory: string | null;
} {
  if (
    !flowData ||
    flowData.flow_type === 'rag' ||
    flowData.flow_type === 'api_server' ||
    flowData.excludeFlowControlNode
  ) {
    return {
      shouldRenderRun: false,
      shouldRenderCron: false,
      connectionCategory: null,
    };
  }

  // BYO (custom) flows expose schedule + run-now controls (unless the
  // flow is archived/restoring, which can never run) — the source
  // can be manually triggered and the user needs the cron node to configure
  // a schedule even before one is set. Gated on UI_ENABLE_BYO_V2 — with the
  // flag off, a flow_type: 'custom' flow (which may predate this v2 rollout)
  // falls through to the same generic connectionCategory-based calculation
  // every other flow type uses below, matching pre-v2 behavior exactly since
  // this whole branch never existed before this rollout.
  if (flowData.flow_type === 'custom' && byoV2Enabled) {
    const canRun = canActivateResource(flowData).canActivate;
    return { shouldRenderRun: canRun, shouldRenderCron: canRun, connectionCategory: null };
  }

  const connectionType = getConnectionOrVendorType(flowData, vendors, 'source_type', 'vendor_id');
  const behaviorType = isSqlAutomation(flowData, vendors) ? 'sql_runner' : connectionType;
  const connectionCategory = metaInfoById[behaviorType]?.connectionCategory ?? null;

  const isSamplingMode = flowData.ingestion_mode === 'sampling';
  let shouldRenderRun =
    !isSamplingMode && shouldRenderRunAction(flowData, connectionCategory || undefined);
  const shouldRenderCron =
    !isSamplingMode && shouldRenderCronAction(flowData, connectionCategory || '');

  if (isCapsuleCluster(flowData) && !hasDestination) {
    shouldRenderRun = false;
  }

  return { shouldRenderRun, shouldRenderCron, connectionCategory };
}

/**
 * Flow metadata may live only on the flow container node when `pipelineItems` is a
 * flat branch keyed by resource ids (no row keyed by the flow's `flow_node_id`).
 */
function resolveFlowRowForRoot(
  pipelineItems: PipelineItems,
  flowRootKey: string,
  newNodes: Node<any>[],
): any {
  const fromMap = pipelineItems[flowRootKey];
  if (fromMap != null) return fromMap;
  const container = newNodes.find((n) => n.id === `flow-${flowRootKey}`);
  return container?.data?.item;
}

export function updateNodesWithControlNodes({
  pipelineItems,
  newNodes,
  newEdges,
  props,
  nodeProps,
  vendors,
  metaInfoById,
  layoutDirection = 'LR',
  entities,
  branches,
}: BuildControlNodesProps) {
  // Add control nodes for each source in the flow
  if (pipelineItems && Object.keys(pipelineItems).length > 0) {
    // Find actual source resource nodes only (not flow containers, credentials, or control nodes).
    // Resource nodes have IDs like 'node-{flowNodeId}' or 'node-SOURCE-{id}'.
    const sourceNodes = newNodes.filter((node) => {
      if (!node.id.startsWith('node-')) return false;
      if (node.id.startsWith('node-SOURCE-')) return true; // flat layout source
      return isPipelineSourceItem((node.data as any)?.item);
    });

    sourceNodes.forEach((sourceNode) => {
      const pipelineSourceItem = (sourceNode?.data as any)?.item;
      // Merge with full source entity from Redux to get complete config data
      // (pipeline items may lack source_config, template_config, etc.)
      const sourceEntity = pipelineSourceItem?.id && entities?.sources?.[pipelineSourceItem.id];
      const sourceItem = sourceEntity
        ? { ...pipelineSourceItem, ...sourceEntity }
        : pipelineSourceItem;

      // Resolve flow data from the flow container or flow entity
      const flowRootKey = sourceNode.parentId ? sourceNode.parentId.replace(/^flow-/, '') : null;
      const flowRow = flowRootKey
        ? resolveFlowRowForRoot(pipelineItems, flowRootKey, newNodes)
        : null;

      const reduxFlowGraphKey = getScheduleCanvasGraphKey(sourceItem, props?.id);

      const oid = sourceItem?.origin_node_id;
      const reduxFlowRow = oid != null && entities?.flowNodes ? entities.flowNodes[oid] : undefined;
      const flowData = {
        ...(flowRow || {}),
        ...(reduxFlowRow || {}),
        ...sourceItem,
        flow_type: flowRow?.flow_type ?? reduxFlowRow?.flow_type ?? sourceItem?.flow_type,
        // ponytail: props/nodeProps take precedence so modal's excludeFlowControlNode=true suppresses cron/run
        excludeFlowControlNode:
          props.excludeFlowControlNode ||
          nodeProps?.excludeFlowControlNode ||
          flowRow?.excludeFlowControlNode ||
          reduxFlowRow?.excludeFlowControlNode ||
          sourceItem?.excludeFlowControlNode,
      };

      const hasDestination = (branches?.[oid]?.items || []).some(
        (key) => entities?.flowNodes?.[key]?.type === 'DATA_SINK',
      );

      const { shouldRenderRun, shouldRenderCron, connectionCategory } = getScheduleCanvasVisibility(
        flowData,
        vendors,
        metaInfoById,
        hasDestination,
        nodeProps?.byoV2Enabled,
      );

      if (!shouldRenderRun && !shouldRenderCron) {
        return;
      }

      const sourceNodeId = sourceNode?.id;

      const dirCfg = getDirectionConfig(layoutDirection);
      const fc = dirCfg.flowControl;
      const zSched = FLOW_CANVAS_CONFIG.shared.scheduleControl.zIndex;

      const baseData = {
        ...flowData,
        isControlNode: true,
        isScheduleCanvasNode: true,
        flowNodeId: reduxFlowGraphKey,
        sourceNodeId,
        item: sourceItem,
        connectionCategory,
        resourceId: String(sourceItem?.id ?? ''),
        excludeFlowControlNode: props.excludeFlowControlNode || nodeProps?.excludeFlowControlNode,
        layoutDirection,
        width: fc.widthEstimate,
        height: fc.height,
        // Read by LastRunNode's own byoRunGate — see that component for why it
        // needs this in addition to the shouldRenderRun/shouldRenderCron check
        // just above.
        byoV2Enabled: nodeProps?.byoV2Enabled,
      };

      // Control nodes share the same parent (flow container) as the source node
      // so they're in the same coordinate space for positioning.
      const flowParentId = sourceNode?.parentId;

      const pushControlNode = (
        controlType: 'runNow' | 'cronJob',
        nodeId: string,
        reactFlowType: 'last_run' | 'cron_job',
      ) => {
        newNodes.push({
          id: nodeId,
          type: reactFlowType,
          draggable: true,
          selectable: true,
          position: { x: 0, y: 0 },
          ...(flowParentId ? { parentId: flowParentId } : {}),
          zIndex: zSched,
          data: { ...baseData, controlType },
        });
      };

      if (shouldRenderRun) {
        pushControlNode('runNow', `run-now-${reduxFlowGraphKey}`, 'last_run');
      }
      if (shouldRenderCron) {
        pushControlNode('cronJob', `cron-${reduxFlowGraphKey}`, 'cron_job');
      }
    });
  }
}
